import { FC, useCallback, useState } from 'react';

import { AvailableCurrencies, CURRENCY_SYMBOLS } from 'utils/clickme';

import cpcQuickBudgetViewButtonClick from '@hh.ru/analytics-js-events/build/clickme/employer/campaign_menu/cpc_quick/cpc_quick_budget_view_button_click';
import { Link, NumberInput, VSpacing, VSpacingContainer } from '@hh.ru/magritte-ui';

import useTrls from 'hooks/translation/useTrls';

import { MagritteFormField } from 'components/common/MagritteFormField/MagritteFormField';

import { QuickCampaignFieldName, setQuickCampaignFieldByName } from 'store/models/createQuickCampaign';
import { useDispatch, useSelector } from 'store/utils';

const TrlKeys = {
    totalBudget: 'create.campaign.input.totalBudget.label',
    dailyBudget: 'create.campaign.input.dailyBudget.label',
    hint: 'create.campaign.input.budget.hint',
    currency: 'currency',
    switchStandard: 'create.campaign.budget.switch.standard.link',
    switchAdvanced: 'create.campaign.budget.switch.advanced.link',
};

export const CampaignBudget: FC = () => {
    const { config, formData, formErrors } = useSelector((state) => state.createQuickCampaign);

    const dispatch = useDispatch();

    const [isAdvancedSettings, setAdvancedSettings] = useState(false);

    const trl = useTrls();
    const quickStartType = formData[QuickCampaignFieldName.QuickStartType];
    const totalBudget = formData[QuickCampaignFieldName.TotalBudget];
    const dailyBudget = formData[QuickCampaignFieldName.DailyBudget];
    const vacancyId = formData[QuickCampaignFieldName.VacancyId];

    const errorTotalBudget = formErrors[QuickCampaignFieldName.TotalBudget];
    const errorDailyBudget = formErrors[QuickCampaignFieldName.DailyBudget];

    const onChangeBudget = useCallback(
        (fieldName: QuickCampaignFieldName, value: string) =>
            dispatch(setQuickCampaignFieldByName({ fieldName, value: Number(value) })),
        [dispatch]
    );

    const configItem = config.find(({ type }) => type === quickStartType);

    const { minBudget, minBudgetDaily } = configItem || {};

    const switchSettings = useCallback(() => {
        if (isAdvancedSettings) {
            cpcQuickBudgetViewButtonClick({ vacancyId: Number(vacancyId), viewType: 'standard' });
            dispatch(setQuickCampaignFieldByName({ fieldName: QuickCampaignFieldName.DailyBudget, value: null }));
        } else {
            cpcQuickBudgetViewButtonClick({ vacancyId: Number(vacancyId), viewType: 'advanced' });
            dispatch(
                setQuickCampaignFieldByName({
                    fieldName: QuickCampaignFieldName.DailyBudget,
                    value: minBudgetDaily ?? 0,
                })
            );
        }
        setAdvancedSettings(!isAdvancedSettings);
    }, [isAdvancedSettings, vacancyId, dispatch, minBudgetDaily]);

    const totalBudgetInput = (
        <NumberInput
            value={String(totalBudget ?? minBudget)}
            postfix={CURRENCY_SYMBOLS[AvailableCurrencies.RUR]}
            onChange={(value: string) => onChangeBudget(QuickCampaignFieldName.TotalBudget, value)}
            invalid={!!errorTotalBudget}
            errorMessage={errorTotalBudget}
            data-qa="total-budget-input"
            clearableOnFocus
            allowNegative={false}
            decimalLength={0}
            groupSize={Infinity}
            decimalMark={[]}
        />
    );

    const dailyBudgetInput = (
        <NumberInput
            value={String(dailyBudget ?? minBudgetDaily)}
            postfix={CURRENCY_SYMBOLS[AvailableCurrencies.RUR]}
            onChange={(value: string) => onChangeBudget(QuickCampaignFieldName.DailyBudget, value)}
            invalid={!!errorDailyBudget}
            errorMessage={errorDailyBudget}
            data-qa="daily-budget-input"
            clearableOnFocus
            allowNegative={false}
            decimalLength={0}
            groupSize={Infinity}
            decimalMark={[]}
        />
    );

    const content = isAdvancedSettings ? (
        <VSpacingContainer default={32}>
            <MagritteFormField label={trl(TrlKeys.totalBudget)} input={totalBudgetInput} />
            <MagritteFormField label={trl(TrlKeys.dailyBudget)} input={dailyBudgetInput} />
        </VSpacingContainer>
    ) : (
        <MagritteFormField label={trl(TrlKeys.totalBudget)} input={totalBudgetInput} />
    );

    return (
        <div>
            {content}
            <VSpacing default={12} />
            <Link
                Element="button"
                onClick={switchSettings}
                data-qa={isAdvancedSettings ? 'budget-switch-standard-link' : 'budget-switch-advanced-link'}
            >
                {trl(isAdvancedSettings ? TrlKeys.switchStandard : TrlKeys.switchAdvanced)}
            </Link>
            <VSpacing default={32} />
        </div>
    );
};
